import React, { useRef, useState, useEffect } from "react";
import { Bold, Italic, Strikethrough, List, ListOrdered, Link2, Code, Minus, ChevronDown, MoreVertical, Quote, Underline } from "lucide-react";
import { cryptiqueAnalyticsTheme as theme } from "../theme";

const TOOLBAR_HEIGHT = 40;
const GAP = 6;

const TEXT_STYLES = [
  { id: "t1", label: "T1 Title" },
  { id: "t2", label: "T2 Subtitle" },
  { id: "text", label: "T Text" },
];

/**
 * Bubble toolbar matching Mixpanel: T2 dropdown, list, B/I/S, code, link, more.
 * T1/T2/T Text set the card's text size (onTextStyleChange), not inline formatBlock.
 */
export default function RichTextToolbar({ editorRef, visible, selectionRect, restoreSelection, onAfterAction, onToolbarInteraction, onTextStyleChange, currentTextStyle = "text" }) {
  const toolbarRef = useRef(null);
  const [textStyleOpen, setTextStyleOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const exec = (command, value = null) => {
    if (!editorRef?.current) return;
    editorRef.current.focus();
    restoreSelection?.();
    // Defer execCommand so the restored selection is applied before the command runs (fixes lists/bold/etc.)
    requestAnimationFrame(() => {
      if (!editorRef?.current) return;
      document.execCommand(command, false, value ?? undefined);
      onAfterAction?.();
    });
  };

  /** Prevents focus moving to the button so the editor keeps selection when applying format */
  const preventFocusLoss = (e) => e.preventDefault();

  /** Run list command synchronously in mousedown so selection is never lost (execCommand must run in same user interaction) */
  const execListOnMouseDown = (e, command) => {
    e.preventDefault();
    e.stopPropagation();
    if (!editorRef?.current) return;
    editorRef.current.focus();
    restoreSelection?.();
    document.execCommand(command, false, null);
    onAfterAction?.();
  };

  const runAndClose = (fn) => {
    fn();
    setTextStyleOpen(false);
    setMoreOpen(false);
  };

  const handleLink = () => {
    const url = window.prompt("Enter URL:");
    if (url != null) exec("createLink", url.trim() || "https://");
    onAfterAction?.();
  };

  useEffect(() => {
    if (!visible) {
      setTextStyleOpen(false);
      setMoreOpen(false);
    }
  }, [visible]);

  useEffect(() => {
    const close = (e) => {
      if (toolbarRef.current && e.target && toolbarRef.current.contains(e.target)) return;
      setTextStyleOpen(false);
      setMoreOpen(false);
    };
    if (textStyleOpen || moreOpen) {
      const t = setTimeout(() => document.addEventListener("click", close), 0);
      return () => {
        clearTimeout(t);
        document.removeEventListener("click", close);
      };
    }
  }, [textStyleOpen, moreOpen]);

  const style = {
    display: visible ? "flex" : "none",
    position: "fixed",
    zIndex: 1000,
    backgroundColor: theme.bgRaised,
    borderColor: theme.border,
    color: theme.textPrimary,
  };

  if (visible && selectionRect) {
    let top = selectionRect.top - TOOLBAR_HEIGHT - GAP;
    const left = Math.max(8, Math.min(selectionRect.left, typeof window !== "undefined" ? window.innerWidth - 340 : selectionRect.left));
    if (top < 8) top = selectionRect.bottom + GAP;
    style.top = `${top}px`;
    style.left = `${left}px`;
  }

  return (
    <div
      ref={toolbarRef}
      className="flex items-center gap-0.5 px-2 py-1.5 rounded-lg border shadow-lg"
      style={style}
      onMouseDown={(e) => {
        e.preventDefault();
        onToolbarInteraction?.();
      }}
    >
      {/* T2 – Text style dropdown: sets card text size (T1 Title, T2 Subtitle, T Text) */}
      <div className="relative">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setTextStyleOpen((o) => !o); setMoreOpen(false); }}
          className="flex items-center gap-0.5 px-2 py-1.5 rounded hover:bg-black/10 transition-colors text-xs font-medium"
          style={{ color: theme.textPrimary }}
          title="Text style"
        >
          {currentTextStyle === "t1" ? "T1" : currentTextStyle === "t2" ? "T2" : "T"}
          <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        </button>
        {textStyleOpen && (
          <div
            className="absolute left-0 top-full mt-0.5 py-1 rounded-lg border shadow-lg min-w-[140px] z-10"
            style={{ backgroundColor: theme.bgRaised, borderColor: theme.border }}
          >
            {TEXT_STYLES.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  runAndClose(() => onTextStyleChange?.(id));
                }}
                className="w-full px-3 py-1.5 text-left text-sm hover:bg-black/10 transition-colors"
                style={{ color: theme.textPrimary }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* List (bullet / numbered) – run in mousedown synchronously so selection is preserved */}
      <button type="button" onMouseDown={(e) => execListOnMouseDown(e, "insertUnorderedList")} className="p-1.5 rounded hover:bg-black/10 transition-colors" style={{ color: theme.textPrimary }} title="Bullet list">
        <List className="w-4 h-4" />
      </button>
      <button type="button" onMouseDown={(e) => execListOnMouseDown(e, "insertOrderedList")} className="p-1.5 rounded hover:bg-black/10 transition-colors" style={{ color: theme.textPrimary }} title="Numbered list">
        <ListOrdered className="w-4 h-4" />
      </button>

      <span className="w-px h-4 mx-0.5 opacity-40" style={{ backgroundColor: theme.textMuted }} />

      {/* Bold, Italic, Strikethrough */}
      <button type="button" onMouseDown={preventFocusLoss} onClick={() => exec("bold")} className="p-1.5 rounded hover:bg-black/10 transition-colors" style={{ color: theme.textPrimary }} title="Bold">
        <Bold className="w-4 h-4" />
      </button>
      <button type="button" onMouseDown={preventFocusLoss} onClick={() => exec("italic")} className="p-1.5 rounded hover:bg-black/10 transition-colors" style={{ color: theme.textPrimary }} title="Italic">
        <Italic className="w-4 h-4" />
      </button>
      <button type="button" onMouseDown={preventFocusLoss} onClick={() => exec("strikeThrough")} className="p-1.5 rounded hover:bg-black/10 transition-colors" style={{ color: theme.textPrimary }} title="Strikethrough">
        <Strikethrough className="w-4 h-4" />
      </button>

      {/* Code */}
      <button type="button" onMouseDown={preventFocusLoss} onClick={() => exec("formatBlock", "<pre>")} className="p-1.5 rounded hover:bg-black/10 transition-colors" style={{ color: theme.textPrimary }} title="Code">
        <Code className="w-4 h-4" />
      </button>

      {/* Link */}
      <button type="button" onMouseDown={preventFocusLoss} onClick={handleLink} className="p-1.5 rounded hover:bg-black/10 transition-colors" style={{ color: theme.textPrimary }} title="Link">
        <Link2 className="w-4 h-4" />
      </button>

      {/* More (⋯) – Ordered List, Block Quote, Divider Line, Underline, Code */}
      <div className="relative">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setMoreOpen((o) => !o); setTextStyleOpen(false); }}
          className="p-1.5 rounded hover:bg-black/10 transition-colors"
          style={{ color: theme.textPrimary }}
          title="More"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        {moreOpen && (
          <div
            className="absolute right-0 top-full mt-0.5 py-1 rounded-lg border shadow-lg min-w-[180px] z-10"
            style={{ backgroundColor: theme.bgRaised, borderColor: theme.border }}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); runAndClose(() => exec("insertOrderedList")); }}
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-black/10 transition-colors flex items-center gap-2"
              style={{ color: theme.textPrimary }}
            >
              <ListOrdered className="w-4 h-4 flex-shrink-0" />
              Ordered List
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); runAndClose(() => exec("formatBlock", "<blockquote>")); }}
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-black/10 transition-colors flex items-center gap-2"
              style={{ color: theme.textPrimary }}
            >
              <Quote className="w-4 h-4 flex-shrink-0" />
              Block Quote
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); runAndClose(() => exec("insertHorizontalRule")); }}
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-black/10 transition-colors flex items-center gap-2"
              style={{ color: theme.textPrimary }}
            >
              <Minus className="w-4 h-4 flex-shrink-0" />
              Divider Line
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); runAndClose(() => exec("underline")); }}
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-black/10 transition-colors flex items-center gap-2"
              style={{ color: theme.textPrimary }}
            >
              <Underline className="w-4 h-4 flex-shrink-0" />
              Underline
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); runAndClose(() => exec("formatBlock", "<pre>")); }}
              className="w-full px-3 py-1.5 text-left text-sm hover:bg-black/10 transition-colors flex items-center gap-2"
              style={{ color: theme.textPrimary }}
            >
              <Code className="w-4 h-4 flex-shrink-0" />
              Code
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
